// EVH (Email Verification Hardening) — /verify-email-pending page.
//
// Rendered after a soft-login is issued (register or login of an
// unverified user). Shows the pending email, lets the user:
//   • resend the verification email,
//   • correct the email (only allowed while unverified — enforced
//     server-side; the page also disables the form when verified),
//   • log out.
//
// Auth: backed by a soft-token (different signature than the canonical
// JWT). Only the three EVH endpoints accept it; everything else returns
// 401. The page therefore stays self-contained and never depends on
// /auth/me or any other authenticated route.
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/i18n";
import PublicLayout from "@/components/layout/PublicLayout";
import { toast } from "sonner";
import { extractErrorMessage } from "@/lib/api";
import { Mail, AlertCircle, LogOut } from "lucide-react";

const VerifyEmailPending = () => {
  const { t } = useLang();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null); // { email, email_verified, requires_email_verification, soft_session }
  const [newEmail, setNewEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [devUrl, setDevUrl] = useState(null);

  useEffect(() => {
    let mounted = true;
    api.get("/auth/verify-status")
      .then((r) => {
        if (!mounted) return;
        setStatus(r.data);
        // If the user is already verified, this page has nothing to do —
        // bounce them to /login so they can start a clean full session.
        if (r.data?.email_verified) {
          logout();
          toast.success(t("auth.verifySuccess"));
          navigate("/login");
        }
      })
      .catch(() => {
        // Soft token expired / invalid → boot to login.
        logout();
        navigate("/login");
      })
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [logout, navigate, t]);

  const handleResend = async () => {
    if (!status?.email) return;
    setResending(true);
    setDevUrl(null);
    try {
      // /auth/resend-verification is public and email-keyed — it does
      // not require the soft token. We pass the user's pending email
      // so the backend re-issues the verification token to the right
      // user. In MOCK MODE the server logs the email and we surface
      // the dev link only when the response includes it.
      await api.post("/auth/resend-verification", { email: status.email });
      toast.success(t("auth.evhResendSent"));
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setResending(false);
    }
  };

  const handleCorrect = async (e) => {
    e.preventDefault();
    if (!newEmail) return;
    setSubmitting(true);
    setDevUrl(null);
    try {
      const { data } = await api.post("/auth/correct-email", { new_email: newEmail });
      if (data?.updated) {
        toast.success(t("auth.evhCorrectSuccess"));
        setStatus((s) => ({ ...(s || {}), email: data.email }));
        setNewEmail("");
        if (data.dev_verify_url) setDevUrl(data.dev_verify_url);
      }
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  if (loading) {
    return (
      <PublicLayout>
        <div className="tsw-section py-20 max-w-lg text-center">
          <p className="text-slate-600">{t("common.loading")}</p>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <div className="tsw-section py-16 max-w-xl" data-testid="verify-email-pending-page">
        <div className="mb-8">
          <div className="tsw-overline mb-2">{t("auth.verifyTitle")}</div>
          <h1 className="text-3xl sm:text-4xl font-display font-bold text-slate-900">
            {t("auth.evhPendingTitle")}
          </h1>
          <p className="text-slate-600 mt-3">{t("auth.evhPendingSub")}</p>
        </div>

        <div className="tsw-card p-6 sm:p-7 space-y-5">
          <div className="flex items-start gap-3 text-slate-700">
            <Mail className="w-5 h-5 text-emerald-700 mt-0.5" />
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500">
                {t("auth.evhPendingEmail")}
              </div>
              <div
                className="text-lg font-medium text-slate-900 break-all"
                data-testid="verify-email-pending-email"
              >
                {status?.email}
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={handleResend}
            disabled={resending}
            className="tsw-btn-secondary w-full"
            data-testid="verify-email-pending-resend"
          >
            {resending ? t("common.loading") : t("auth.evhResendBtn")}
          </button>
        </div>

        <div className="tsw-card p-6 sm:p-7 mt-5 space-y-4">
          <div className="flex items-center gap-2 text-emerald-800">
            <AlertCircle className="w-4 h-4" />
            <h2 className="text-base font-semibold">{t("auth.evhCorrectTitle")}</h2>
          </div>
          <form onSubmit={handleCorrect} className="space-y-4" data-testid="verify-email-pending-correct-form">
            <div>
              <label className="tsw-label">{t("auth.evhCorrectNewLabel")}</label>
              <input
                type="email"
                required
                className="tsw-input"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                data-testid="verify-email-pending-new-email"
              />
            </div>
            <button
              type="submit"
              disabled={submitting || !newEmail}
              className="tsw-btn-primary w-full"
              data-testid="verify-email-pending-correct-submit"
            >
              {submitting ? t("common.loading") : t("auth.evhCorrectSubmit")}
            </button>
          </form>
          {devUrl && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm">
              <div className="text-amber-900 font-medium mb-1">
                {t("auth.evhDevTokenLabel")}
              </div>
              <a
                href={devUrl}
                className="text-emerald-800 underline break-all"
                data-testid="verify-email-pending-dev-link"
              >
                {devUrl}
              </a>
            </div>
          )}
        </div>

        <div className="mt-6 text-center">
          <button
            type="button"
            onClick={handleLogout}
            className="text-sm text-slate-600 hover:text-slate-900 inline-flex items-center gap-1.5"
            data-testid="verify-email-pending-logout"
          >
            <LogOut className="w-4 h-4" />
            {t("auth.evhLogoutBtn")}
          </button>
        </div>
      </div>
    </PublicLayout>
  );
};

export default VerifyEmailPending;
